import _defineProperty from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["perksCount", "performMutation", "profileId", "rewardPoints", "history", "queriesToRefetchOnRedeem"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { makeReference } from '@apollo/client';
import { AppAlertsContext } from '@infl-ts/ui-app-alerts';
import { useToggle } from '@infl-ts/util-hooks';
import * as A from 'fp-ts/Array';
import * as E from 'fp-ts/Either';
import { flow, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import * as Pred from 'fp-ts/Predicate';
import * as Str from 'fp-ts/string';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { rewardFailure, rewardSuccess, unknownFailure } from '../reward-card/alerts';
import { UnavailableInLens } from '../unavailable-in-lens';
import { useRedeemRewardMutation } from './mutations';
import RedeemButton from './redeem-button';
import RedeemDialog from './redeem-dialog';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

var PureRewardRedemption = function PureRewardRedemption(_ref) {
  var _ref$reward = _ref.reward,
      name = _ref$reward.name,
      requiresShippingAddress = _ref$reward.requiresShippingAddress,
      questions = _ref$reward.questions,
      points = _ref$reward.points,
      perksCount = _ref$reward.perksCount,
      isOpen = _ref.isOpen,
      setOpen = _ref.setOpen,
      setClosed = _ref.setClosed,
      redeemReward = _ref.redeemReward,
      mutationStatus = _ref.mutationStatus,
      isPerk = _ref.isPerk,
      isAdmin = _ref.isAdmin,
      canAfford = _ref.canAfford,
      address = _ref.address,
      currentPoints = _ref.currentPoints,
      requiresAnswers = _ref.requiresAnswers;
  return /*#__PURE__*/_jsxs(UnavailableInLens, {
    tooltip: true,
    "data-element": "UnavailableInLens",
    "data-component": "PureRewardRedemption",
    "data-source-file": "reward-redemption.tsx",
    children: [/*#__PURE__*/_jsx(RedeemButton, {
      onClick: !isPerk || requiresAnswers ? setOpen : function () {
        return redeemReward();
      },
      mutationStatus: mutationStatus,
      tooltipText: isAdmin ? "Admins cannot redeem ".concat(isPerk ? 'gifts' : 'rewards') : '',
      disabled: isAdmin || !(canAfford || isPerk),
      redeemText: isPerk ? 'Accept' : 'Redeem',
      "data-element": "RedeemButton",
      "data-component": "PureRewardRedemption",
      "data-source-file": "reward-redemption.tsx"
    }), /*#__PURE__*/_jsx(RedeemDialog, {
      address: address,
      title: name,
      redeemText: isPerk ? 'Redeem gift' : 'Redeem reward',
      open: isOpen,
      points: points,
      currentPoints: currentPoints,
      onClose: setClosed,
      questions: questions,
      perksCount: perksCount,
      handleRedeem: function handleRedeem(input) {
        setClosed();
        return redeemReward(input);
      },
      requiresShippingAddress: requiresShippingAddress,
      "data-element": "RedeemDialog",
      "data-source-file": "reward-redemption.tsx"
    })]
  });
};

var alreadyOnRewardsPage = pipe(Str.endsWith('/rewards/wishlist/'), Pred.or(Str.endsWith('/rewards')));

var handleRedeem = function handleRedeem(_ref2) {
  var perksCount = _ref2.perksCount,
      performMutation = _ref2.performMutation,
      profileId = _ref2.profileId,
      rewardPoints = _ref2.rewardPoints,
      history = _ref2.history,
      queriesToRefetchOnRedeem = _ref2.queriesToRefetchOnRedeem,
      input = _objectWithoutProperties(_ref2, _excluded);

  return performMutation({
    variables: {
      input: input
    },
    update: function update(cache, data) {
      return pipe(data, E.match(function (e) {
        return console.error(e);
      }, function (_ref3) {
        var redeemReward = _ref3.redeemReward;
        var noPerksLeft = perksCount - 1 === 0;

        if (redeemReward.__typename === 'RedeemRewardResult') {
          cache.evict({
            id: cache.identify(makeReference('ROOT_QUERY')),
            fieldName: 'rewards'
          });
          cache.gc();

          if (noPerksLeft && !alreadyOnRewardsPage(history.location.pathname)) {
            // TODO: this can't be hardcoded to /rewards when we do pages.
            history.push('/rewards');
          }
        }
      }));
    },
    refetchQueries: queriesToRefetchOnRedeem
  });
};

var useProps = function useProps(_ref4) {
  var reward = _ref4.reward,
      isAdmin = _ref4.isAdmin,
      canAfford = _ref4.canAfford,
      address = _ref4.address,
      currentPoints = _ref4.currentPoints,
      profileId = _ref4.profileId,
      queriesToRefetchOnRedeem = _ref4.queriesToRefetchOnRedeem;

  var _useToggle = useToggle(),
      _useToggle2 = _slicedToArray(_useToggle, 2),
      isOpen = _useToggle2[0],
      _useToggle2$ = _useToggle2[1],
      setOpen = _useToggle2$.on,
      setClosed = _useToggle2$.off;

  var history = useHistory();

  var _useContext = useContext(AppAlertsContext),
      showAlert = _useContext.showAlert;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var requiresAnswers = reward.questions && !A.isEmpty(reward.questions) || reward.requiresShippingAddress;
  var isPerk = reward.perksCount > 0;

  var _useRedeemRewardMutat = useRedeemRewardMutation({
    onCompleted: E.match(function () {
      return showAlert(unknownFailure(t));
    }, flow(O.fromPredicate(function (_ref5) {
      var __typename = _ref5.redeemReward.__typename;
      return __typename === 'RedeemRewardResult';
    }), O.matchW(function () {
      return rewardFailure(isPerk, requiresAnswers ? setOpen : function () {
        return handleRedeem({
          id: reward.id,
          perksCount: reward.perksCount,
          profileId: profileId,
          rewardPoints: reward.points,
          performMutation: performMutation,
          history: history,
          queriesToRefetchOnRedeem: queriesToRefetchOnRedeem
        });
      }, t);
    }, rewardSuccess(isPerk, t)), showAlert))
  }),
      _useRedeemRewardMutat2 = _slicedToArray(_useRedeemRewardMutat, 2),
      performMutation = _useRedeemRewardMutat2[0],
      mutationStatus = _useRedeemRewardMutat2[1];

  return {
    reward: reward,
    isOpen: isOpen,
    setOpen: setOpen,
    setClosed: setClosed,
    mutationStatus: mutationStatus,
    redeemReward: function redeemReward(input) {
      return handleRedeem(_objectSpread({
        id: reward.id,
        perksCount: reward.perksCount,
        profileId: profileId,
        rewardPoints: reward.points,
        performMutation: performMutation,
        history: history,
        queriesToRefetchOnRedeem: queriesToRefetchOnRedeem
      }, input));
    },
    isPerk: isPerk,
    isAdmin: isAdmin,
    canAfford: canAfford,
    address: address,
    currentPoints: currentPoints,
    requiresAnswers: requiresAnswers
  };
};

export default Object.assign(flow(useProps, PureRewardRedemption), {
  displayName: 'RewardRedemption'
});